import { z } from "zod";
import type { RepairIntakeFormData } from "@/store/useRepairIntakeStore";

const fileSchema = z.custom<File>((value) => value instanceof File, {
  message: "Invalid file",
});

const requiredText = z.string().trim().min(1, "This field is required");
const requiredYesNoChoice = z
  .union([z.literal("Yes"), z.literal("No"), z.literal("")])
  .refine((value) => value === "Yes" || value === "No", {
    message: "Please select an option",
  });

export const repairIntakeSchema = z
  .object({
    section1: z.object({
      componentTypes: z.array(z.string()).min(1, "Select at least one component type"),
      componentTypeOther: z.string().optional(),
      materialsGrades: requiredText,
      componentEntries: z
        .array(
          z.object({
            nominalPipeSize: requiredText,
            outsideDiameter: requiredText,
            schedule: requiredText,
            nominalWallThickness: requiredText,
          })
        )
        .min(1, "Add at least one component"),
    }),
    section2: z.object({
      utReadingsAttached: requiredYesNoChoice,
      utThickness: requiredText,
      deepestCorrosionDepth: z.string(),
    }),
    section3: z.object({
      repairTypes: z.array(z.string()).min(1, "Select at least one repair type"),
      repairTypeOther: z.string().optional(),
      designPressure: requiredText,
      designTemperature: requiredText,
      operatingPressure: requiredText,
      operatingTemperature: requiredText,
    }),
    section4: z.object({
      serviceFluid: requiredText,
      sdsAttached: requiredYesNoChoice,
    }),
    section5: z.object({
      defectTypes: z.array(z.string()).min(1, "Select at least one defect type"),
      defectTypeOther: z.string().optional(),
      defectLengthEntries: z
        .array(
          z.object({
            componentLabel: z.string(),
            diameterOrNPS: requiredText,
            defectLength: requiredText,
          })
        )
        .min(1, "Add at least one component"),
      holeOrSlotSize: z.string(),
      desiredRepairLengthEntries: z
        .array(
          z.object({
            componentLabel: z.string(),
            diameterOrNPS: requiredText,
            desiredRepairLength: requiredText,
          })
        )
        .min(1, "Add at least one component"),
    }),
    section6: z.object({
      activeLeak: requiredYesNoChoice,
      leakMitigationPlan: z.string(),
      clientLeakStopPlan: z.string(),
    }),
    section7: z.object({
      installedUnderPressure: requiredYesNoChoice,
      desiredRepairLife: requiredText,
      desiredRepairLifeOther: z.string().optional(),
      goodPipeOnLandingZones: requiredYesNoChoice,
      inchesOfGoodPipe: z.string(),
      defectsFieldVerified: requiredYesNoChoice,
      additionalNotes: requiredText,
    }),
    section8: z.object({
      yourName: z.string(),
      customerName: requiredText,
    }),
    section9: z.object({
      attachments: z.array(fileSchema),
    }),
  })
  .transform((values) => values as RepairIntakeFormData)
  .superRefine((data, ctx) => {
    if (data.section1.componentTypes.includes("Other") && !data.section1.componentTypeOther?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section1", "componentTypeOther"],
      });
    }

    if (data.section2.utReadingsAttached === "No" && !data.section2.deepestCorrosionDepth.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section2", "deepestCorrosionDepth"],
      });
    }

    if (data.section3.repairTypes.includes("Other") && !data.section3.repairTypeOther?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section3", "repairTypeOther"],
      });
    }

    if (data.section5.defectTypes.includes("Other") && !data.section5.defectTypeOther?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section5", "defectTypeOther"],
      });
    }

    if (data.section6.activeLeak === "Yes" && !data.section6.leakMitigationPlan.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section6", "leakMitigationPlan"],
      });
    }
    if (data.section6.activeLeak === "Yes" && !data.section6.clientLeakStopPlan.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section6", "clientLeakStopPlan"],
      });
    }

    if (data.section7.desiredRepairLife === "Other" && !data.section7.desiredRepairLifeOther?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section7", "desiredRepairLifeOther"],
      });
    }
    if (data.section7.goodPipeOnLandingZones === "No" && !data.section7.inchesOfGoodPipe.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["section7", "inchesOfGoodPipe"],
      });
    }
    if (!data.section9.attachments.length) {
      ctx.addIssue({
        code: "custom",
        message: "Add at least one attachment",
        path: ["section9", "attachments"],
      });
    }
    if (data.section9.attachments.length > 10) {
      ctx.addIssue({
        code: "custom",
        message: "Upload up to 10 files",
        path: ["section9", "attachments"],
      });
    }
    data.section9.attachments.forEach((file, index) => {
      if (file.size > 100 * 1024 * 1024) {
        ctx.addIssue({
          code: "custom",
          message: "Each file must be 100 MB or smaller",
          path: ["section9", "attachments", index],
        });
      }
    });
  });
